import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Badge,
  Avatar,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteLogoIcon,
  LocationOn as LocationIcon,
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import { Pagination } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Company } from '../types';
import CertificateDialog from '../components/CertificateDialog';
import SortableTableCell, { toggleSort, SortDirection } from '../components/SortableTableCell';

interface CompanyWithStats extends Company {
  userCount: number;
  subscriptionActive: boolean;
}

const AdminCompanies: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CompanyWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [departmentsDialogOpen, setDepartmentsDialogOpen] = useState(false);
  const [officesDialogOpen, setOfficesDialogOpen] = useState(false);
  const [selectedCompanyForManagement, setSelectedCompanyForManagement] = useState<Company | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 1,
    count: 0,
    totalRecords: 0
  });

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Resetear a la primera página al buscar
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    plan: 'basic' as 'basic' | 'professional' | 'enterprise',
    maxUsers: 50,
    allowSelfRegistration: true,
    requirePasswordComplexity: true,
    sessionTimeout: 30,
    allowViewCertificates: true,
    allowEmployeeChangeCompanyOffice: true,
    defaultPassThreshold: 100,
    coursePassThresholds: [] as Array<{ course: string; passThreshold: number }>,
    evaluationPassThresholds: [] as Array<{ evaluation: string; passThreshold: number }>,
    authorizedDomains: [] as string[],
    logo: '',
    certificateSigner: {
      firstName: '',
      lastName: '',
      position: '',
      signature: ''
    },
    showCTOSignature: true
  });

  // Estado temporal para el campo de dominios
  const [domainsText, setDomainsText] = useState('');
  
  // Estado para el logo
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  
  // Estado para la firma
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string>('');
  const [certificatePreviewOpen, setCertificatePreviewOpen] = useState(false);
  const [availableCourses, setAvailableCourses] = useState<Array<{ id: string; title: string }>>([]);
  const [availableEvaluations, setAvailableEvaluations] = useState<Array<{ id: string; title: string }>>([]);
  const [newCourseThreshold, setNewCourseThreshold] = useState({ course: '', passThreshold: 100 });
  const [newEvalThreshold, setNewEvalThreshold] = useState({ evaluation: '', passThreshold: 100 });

  useEffect(() => {
    fetchCompanies();
  }, [page, debouncedSearchTerm, planFilter, statusFilter, orderBy, order]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.getCompanies({
        page,
        limit: 10,
        search: debouncedSearchTerm || undefined,
        plan: planFilter !== 'all' ? planFilter : undefined,
        isActive: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy: orderBy,
        sortOrder: order
      });
      setCompanies(response.companies);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando empresas');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleRequestSort = (sortKey: string) => {
    const next = toggleSort(orderBy, order, sortKey);
    setOrderBy(next.orderBy);
    setOrder(next.order);
    setPage(1);
  };

  // Función para obtener la URL base del servidor (sin /api)
  const getServerBaseUrl = (): string => {
    // En desarrollo, siempre usar localhost:5000 (puerto del backend)
    if (process.env.NODE_ENV !== 'production') {
      return 'http://localhost:5000';
    }
    
    // En producción, usar la variable de entorno o el mismo origen
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace('/api', '');
    }
    
    return window.location.origin;
  };

  // Función para construir URL completa del logo
  const buildLogoUrl = (logoPath: string | null | undefined): string => {
    if (!logoPath) return '';
    
    // Si ya es una URL completa (http:// o https://), usarla directamente
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    
    // Si es una ruta relativa, construir la URL completa
    const serverBaseUrl = getServerBaseUrl();
    
    // Asegurar que la ruta relativa comience con /
    const logoPathNormalized = logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
    
    return `${serverBaseUrl}${logoPathNormalized}`;
  };

  // Función para construir URL completa de la firma
  const buildSignatureUrl = (signaturePath: string | null | undefined): string => {
    if (!signaturePath) return '';
    
    // Si ya es una URL completa (http:// o https://), usarla directamente
    if (signaturePath.startsWith('http://') || signaturePath.startsWith('https://')) {
      return signaturePath;
    }
    
    // Si es una ruta relativa, construir la URL completa
    const serverBaseUrl = getServerBaseUrl();
    
    // Asegurar que la ruta relativa comience con /
    const signaturePathNormalized = signaturePath.startsWith('/') ? signaturePath : `/${signaturePath}`;
    
    return `${serverBaseUrl}${signaturePathNormalized}`;
  };

  const handleOpenDialog = (company?: Company) => {
    if (company) {
      console.log('🏢 Abriendo empresa para editar:', company.name);
      console.log('📷 Logo de la empresa (raw):', company.logo);
      console.log('📷 Logo URL construida:', buildLogoUrl(company.logo));
      
      setEditingCompany(company);
      setFormData({
        name: company.name,
        email: company.email,
        phone: company.phone || '',
        address: company.address || '',
        plan: company.subscription.plan,
        maxUsers: company.subscription.maxUsers,
        allowSelfRegistration: company.settings.allowSelfRegistration,
        requirePasswordComplexity: company.settings.requirePasswordComplexity,
        sessionTimeout: company.settings.sessionTimeout,
        allowViewCertificates: company.settings.allowViewCertificates ?? true,
        allowEmployeeChangeCompanyOffice: company.settings.allowEmployeeChangeCompanyOffice ?? true,
        defaultPassThreshold: company.settings.defaultPassThreshold ?? 100,
        coursePassThresholds: [],
        evaluationPassThresholds: (company.settings.evaluationPassThresholds || []).map((item: any) => ({
          evaluation: (item.evaluation?._id || item.evaluation || '').toString(),
          passThreshold: item.passThreshold ?? 100
        })),
        authorizedDomains: company.authorizedDomains || [],
        logo: company.logo || '',
        certificateSigner: {
          firstName: company.certificateSigner?.firstName || '',
          lastName: company.certificateSigner?.lastName || '',
          position: company.certificateSigner?.position || '',
          signature: company.certificateSigner?.signature || ''
        },
        showCTOSignature: company.showCTOSignature ?? true
      });
      setDomainsText((company.authorizedDomains || []).join(', '));
      // Construir URL completa del logo para el preview
      const logoFullUrl = buildLogoUrl(company.logo);
      console.log('📷 Estableciendo logoPreview:', logoFullUrl);
      setLogoPreview(logoFullUrl);
      setLogoFile(null);
      // Construir URL completa de la firma para el preview
      setSignaturePreview(buildSignatureUrl(company.certificateSigner?.signature));
      setSignatureFile(null);
    } else {
      setEditingCompany(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        plan: 'basic',
        maxUsers: 50,
        allowSelfRegistration: true,
        requirePasswordComplexity: true,
        sessionTimeout: 30,
        allowViewCertificates: true,
        allowEmployeeChangeCompanyOffice: true,
        defaultPassThreshold: 100,
        coursePassThresholds: [],
        evaluationPassThresholds: [],
        authorizedDomains: [],
        logo: '',
        certificateSigner: {
          firstName: '',
          lastName: '',
          position: '',
          signature: ''
        },
        showCTOSignature: true
      });
      setDomainsText('');
      setLogoPreview('');
      setLogoFile(null);
      setSignaturePreview('');
      setSignatureFile(null);
      setNewCourseThreshold({ course: '', passThreshold: 100 });
      setNewEvalThreshold({ evaluation: '', passThreshold: 100 });
    }
    setOpenDialog(true);
    loadPassThresholdOptions();
  };

  const loadPassThresholdOptions = async () => {
    try {
      const [coursesRes, evalsRes] = await Promise.all([
        api.getAllCourses().catch(() => ({ courses: [] })),
        api.getEvaluations().catch(() => ({ evaluations: [] }))
      ]);
      const courses = (coursesRes.courses || coursesRes || []).map((c: any) => ({
        id: (c._id || c.id || '').toString(),
        title: c.title
      }));
      const evaluations = (evalsRes.evaluations || evalsRes || []).map((e: any) => ({
        id: (e._id || e.id || '').toString(),
        title: e.title
      }));
      setAvailableCourses(courses.filter((c: any) => c.id));
      setAvailableEvaluations(evaluations.filter((e: any) => e.id));
    } catch (err) {
      console.error('Error cargando cursos/evaluaciones para umbrales:', err);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCompany(null);
    setDomainsText('');
    setLogoPreview('');
    setLogoFile(null);
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setSnackbar({ 
          open: true, 
          message: 'Solo se permiten archivos de imagen', 
          severity: 'error' 
        });
        return;
      }

      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: 'El archivo no puede ser mayor a 5MB', 
          severity: 'error' 
        });
        return;
      }

      setLogoFile(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setSnackbar({ 
          open: true, 
          message: 'Solo se permiten archivos de imagen', 
          severity: 'error' 
        });
        return;
      }

      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: 'El archivo no puede ser mayor a 5MB', 
          severity: 'error' 
        });
        return;
      }

      setSignatureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validaciones básicas
      if (!formData.name.trim()) {
        setSnackbar({ 
          open: true, 
          message: 'El nombre de la empresa es obligatorio', 
          severity: 'error' 
        });
        return;
      }

      if (!formData.email.trim()) {
        setSnackbar({ 
          open: true, 
          message: 'El email es obligatorio', 
          severity: 'error' 
        });
        return;
      }

      // Procesar el texto de dominios antes de enviar
      const processedDomains = domainsText
        .split(',')
        .map(domain => domain.trim())
        .filter(domain => domain.length > 0);

      let logoUrl = formData.logo;

      // Subir logo si se seleccionó un archivo nuevo
      if (logoFile) {
        try {
          logoUrl = await api.uploadLogo(logoFile);
        } catch (uploadError: any) {
          console.error('Error subiendo logo:', uploadError);
          setSnackbar({ 
            open: true, 
            message: 'Error subiendo logo: ' + (uploadError.response?.data?.message || uploadError.message), 
            severity: 'error' 
          });
          return;
        }
      }

      let signatureUrl = formData.certificateSigner.signature;

      // Subir firma si se seleccionó un archivo nuevo
      if (signatureFile) {
        try {
          signatureUrl = await api.uploadSignature(signatureFile);
        } catch (uploadError: any) {
          console.error('Error subiendo firma:', uploadError);
          setSnackbar({ 
            open: true, 
            message: 'Error subiendo firma: ' + (uploadError.response?.data?.message || uploadError.message), 
            severity: 'error' 
          });
          return;
        }
      }

      const companyData = {
        ...formData,
        authorizedDomains: processedDomains,
        logo: logoUrl,
        certificateSigner: {
          firstName: formData.certificateSigner.firstName,
          lastName: formData.certificateSigner.lastName,
          position: formData.certificateSigner.position,
          signature: signatureUrl
        },
        subscription: {
          plan: formData.plan,
          maxUsers: formData.maxUsers
          // No enviamos endDate al actualizar, se maneja manualmente
        },
        settings: {
          allowSelfRegistration: formData.allowSelfRegistration,
          requirePasswordComplexity: formData.requirePasswordComplexity,
          sessionTimeout: formData.sessionTimeout,
          allowViewCertificates: formData.allowViewCertificates,
          allowEmployeeChangeCompanyOffice: formData.allowEmployeeChangeCompanyOffice,
          defaultPassThreshold: formData.defaultPassThreshold ?? 100,
          coursePassThresholds: [],
          evaluationPassThresholds: formData.evaluationPassThresholds || []
        }
      };

      console.log('📤 Enviando datos de empresa:', JSON.stringify(companyData, null, 2));
      
      if (editingCompany) {
        const response = await api.updateCompany(editingCompany._id, companyData);
        console.log('✅ Respuesta de actualización:', response);
        setSnackbar({ open: true, message: 'Empresa actualizada exitosamente', severity: 'success' });
      } else {
        const response = await api.createCompany(companyData);
        console.log('✅ Respuesta de creación:', response);
        setSnackbar({ open: true, message: 'Empresa creada exitosamente', severity: 'success' });
      }

      handleCloseDialog();
      // Esperar un poco antes de recargar para asegurar que el servidor haya procesado
      await fetchCompanies();
      console.log('🔄 Lista de empresas recargada');
    } catch (err: any) {
      console.error('Error guardando empresa:', err);
      console.error('Error response:', err.response?.data);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || err.message || 'Error guardando empresa', 
        severity: 'error' 
      });
    }
  };

  const handleToggleStatus = async (company: Company) => {
    try {
      await api.updateCompanyStatus(company._id, !company.isActive);
      setSnackbar({ 
        open: true, 
        message: `Empresa ${company.isActive ? 'desactivada' : 'activada'} exitosamente`, 
        severity: 'success' 
      });
      fetchCompanies();
    } catch (err: any) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || 'Error actualizando estado', 
        severity: 'error' 
      });
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'basic': return 'default';
      case 'professional': return 'primary';
      case 'enterprise': return 'secondary';
      default: return 'default';
    }
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'basic': return 'Básico';
      case 'professional': return 'Profesional';
      case 'enterprise': return 'Empresarial';
      default: return plan;
    }
  };

  if (initialLoading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Cargando empresas...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Gestión de Empresas
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="large"
          >
            Nueva Empresa
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" sx={{ flex: 1 }}>
                <TextField
                  placeholder="Buscar empresas..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  sx={{ width: 280 }}
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Plan</InputLabel>
                  <Select
                    label="Plan"
                    value={planFilter}
                    onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    label="Estado"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="true">Activas</MenuItem>
                    <MenuItem value="false">Inactivas</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total: {pagination.totalRecords} empresas
              </Typography>
            </Box>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableTableCell label="Empresa" sortKey="name" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                    <SortableTableCell label="Plan" sortKey="plan" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                    <TableCell>Usuarios</TableCell>
                    <SortableTableCell label="Estado" sortKey="status" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                    <TableCell>Suscripción</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                          <CircularProgress size={40} />
                          <Typography variant="body2" color="text.secondary">
                            Cargando empresas...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : companies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No se encontraron empresas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    companies.map((company) => (
                    <TableRow key={company._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {company.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {company.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {company.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPlanLabel(company.subscription.plan)}
                          color={getPlanColor(company.subscription.plan) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PeopleIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {company.userCount} / {company.subscription.maxUsers}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={company.isActive ? 'Activa' : 'Inactiva'}
                          color={company.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {company.subscriptionActive ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <WarningIcon color="warning" fontSize="small" />
                          )}
                          <Typography variant="body2">
                            {company.subscriptionActive ? 'Activa' : 'Expirada'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Ver detalles">
                            <IconButton size="small" color="primary">
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog(company)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Administrar Departamentos">
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => {
                                setSelectedCompanyForManagement(company);
                                setDepartmentsDialogOpen(true);
                              }}
                            >
                              <AccountTreeIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Administrar Sedes">
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => {
                                setSelectedCompanyForManagement(company);
                                setOfficesDialogOpen(true);
                              }}
                            >
                              <LocationIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={company.isActive ? 'Desactivar' : 'Activar'}>
                            <IconButton 
                              size="small" 
                              color={company.isActive ? 'error' : 'success'}
                              onClick={() => handleToggleStatus(company)}
                            >
                              {company.isActive ? <CancelIcon /> : <CheckCircleIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {pagination.total > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pagination.total}
                  page={pagination.current}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Dialog para crear/editar empresa */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCompany ? 'Editar Empresa' : 'Nueva Empresa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Nombre de la empresa"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Teléfono"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <TextField
              fullWidth
              label="Dirección"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="h6" gutterBottom>
                Logo de la Empresa
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {logoPreview && (() => {
                  console.log('Mostrando logo preview:', logoPreview);
                  return (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={logoPreview}
                        alt="Logo de la empresa"
                        sx={{ 
                          width: 200, 
                          height: 120, 
                          objectFit: 'contain',
                          border: '2px solid #e0e0e0',
                          borderRadius: 1,
                          backgroundColor: 'white'
                        }}
                      />
                    <IconButton
                      size="small"
                      sx={{ 
                        position: 'absolute', 
                        top: -8, 
                        right: -8, 
                        backgroundColor: 'error.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'error.dark' }
                      }}
                      onClick={() => {
                        setLogoPreview('');
                        setLogoFile(null);
                        setFormData({ ...formData, logo: '' });
                      }}
                    >
                      <DeleteLogoIcon fontSize="small" />
                    </IconButton>
                    </Box>
                  );
                })()}
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoChange}
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      sx={{ mb: 1 }}
                    >
                      {logoPreview ? 'Cambiar Logo' : 'Subir Logo'}
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Formatos: JPG, PNG, GIF. Máximo 5MB
                  </Typography>
                </Box>
              </Box>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Plan de suscripción</InputLabel>
              <Select
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value as any })}
                label="Plan de suscripción"
              >
                <MenuItem value="basic">Básico</MenuItem>
                <MenuItem value="professional">Profesional</MenuItem>
                <MenuItem value="enterprise">Empresarial</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Máximo de usuarios"
              type="number"
              value={formData.maxUsers}
              onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) })}
              inputProps={{ min: 1 }}
            />
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="h6" gutterBottom>
                Configuración de seguridad
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Timeout de sesión (minutos)"
              type="number"
              value={formData.sessionTimeout}
              onChange={(e) => setFormData({ ...formData, sessionTimeout: parseInt(e.target.value) })}
              inputProps={{ min: 5, max: 480 }}
            />
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowEmployeeChangeCompanyOffice}
                    onChange={(e) => setFormData({ ...formData, allowEmployeeChangeCompanyOffice: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permitir que los empleados cambien su sede y departamento"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Desactívalo si solo los administradores deben asignar sede y departamento a los empleados.
              </Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="h6" gutterBottom>
                Configuración de certificados
              </Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowViewCertificates}
                    onChange={(e) => setFormData({ ...formData, allowViewCertificates: e.target.checked })}
                    color="primary"
                  />
                }
                label="Permitir que los empleados vean sus certificados al finalizar el curso"
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="h6" gutterBottom>
                Porcentaje de aprobación de evaluaciones
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                El curso solo se aprueba si el alumno completa todas las lecciones y aprueba
                todas las evaluaciones. Prioridad del umbral: evaluación específica → valor
                por defecto de la empresa → 100% del sistema.
              </Typography>
              <TextField
                fullWidth
                label="Porcentaje mínimo por defecto (%)"
                type="number"
                value={formData.defaultPassThreshold}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defaultPassThreshold: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                  })
                }
                inputProps={{ min: 0, max: 100 }}
                helperText="Se aplica a todas las evaluaciones de esta empresa, salvo override por evaluación."
                sx={{ mb: 3, maxWidth: 360 }}
              />

              <Typography variant="subtitle1" gutterBottom>
                Overrides por evaluación
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 260, flex: 1 }}>
                  <InputLabel>Evaluación</InputLabel>
                  <Select
                    label="Evaluación"
                    value={newEvalThreshold.evaluation}
                    onChange={(e) =>
                      setNewEvalThreshold({ ...newEvalThreshold, evaluation: e.target.value })
                    }
                  >
                    {availableEvaluations
                      .filter(
                        (ev) =>
                          !formData.evaluationPassThresholds.some(
                            (item) => item.evaluation === ev.id
                          )
                      )
                      .map((ev) => (
                        <MenuItem key={ev.id} value={ev.id}>
                          {ev.title}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <TextField
                  label="%"
                  type="number"
                  value={newEvalThreshold.passThreshold}
                  onChange={(e) =>
                    setNewEvalThreshold({
                      ...newEvalThreshold,
                      passThreshold: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                    })
                  }
                  inputProps={{ min: 0, max: 100 }}
                  sx={{ width: 100 }}
                />
                <Button
                  variant="outlined"
                  disabled={!newEvalThreshold.evaluation}
                  onClick={() => {
                    if (!newEvalThreshold.evaluation) return;
                    setFormData({
                      ...formData,
                      evaluationPassThresholds: [
                        ...formData.evaluationPassThresholds,
                        { ...newEvalThreshold }
                      ]
                    });
                    setNewEvalThreshold({ evaluation: '', passThreshold: 100 });
                  }}
                >
                  Agregar
                </Button>
              </Box>
              {formData.evaluationPassThresholds.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Evaluación</TableCell>
                        <TableCell width={100}>%</TableCell>
                        <TableCell width={60} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.evaluationPassThresholds.map((item) => (
                        <TableRow key={item.evaluation}>
                          <TableCell>
                            {availableEvaluations.find((e) => e.id === item.evaluation)?.title ||
                              item.evaluation}
                          </TableCell>
                          <TableCell>{item.passThreshold}%</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  evaluationPassThresholds:
                                    formData.evaluationPassThresholds.filter(
                                      (x) => x.evaluation !== item.evaluation
                                    )
                                })
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="h6" gutterBottom>
                Información del Firmante para Certificados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure la información de la persona que firmará los certificados de la empresa
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Nombre del firmante"
              value={formData.certificateSigner.firstName}
              onChange={(e) => setFormData({ 
                ...formData, 
                certificateSigner: { ...formData.certificateSigner, firstName: e.target.value }
              })}
            />
            <TextField
              fullWidth
              label="Apellido del firmante"
              value={formData.certificateSigner.lastName}
              onChange={(e) => setFormData({ 
                ...formData, 
                certificateSigner: { ...formData.certificateSigner, lastName: e.target.value }
              })}
            />
            <TextField
              fullWidth
              label="Cargo del firmante"
              value={formData.certificateSigner.position}
              onChange={(e) => setFormData({ 
                ...formData, 
                certificateSigner: { ...formData.certificateSigner, position: e.target.value }
              })}
              placeholder="Ej: Director de Recursos Humanos"
            />
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="subtitle1" gutterBottom>
                Firma del firmante
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {signaturePreview && (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={signaturePreview}
                      alt="Vista previa de la firma"
                      sx={{ 
                        width: 200, 
                        height: 80, 
                        border: '2px solid #e0e0e0',
                        objectFit: 'contain',
                        backgroundColor: 'white'
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{ 
                        position: 'absolute', 
                        top: -8, 
                        right: -8, 
                        backgroundColor: 'error.main',
                        color: 'white',
                        '&:hover': { backgroundColor: 'error.dark' }
                      }}
                      onClick={() => {
                        setSignaturePreview('');
                        setSignatureFile(null);
                        setFormData({ 
                          ...formData, 
                          certificateSigner: { ...formData.certificateSigner, signature: '' }
                        });
                      }}
                    >
                      <DeleteLogoIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="signature-upload"
                    type="file"
                    onChange={handleSignatureChange}
                  />
                  <label htmlFor="signature-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      sx={{ mb: 1 }}
                    >
                      {signaturePreview ? 'Cambiar Firma' : 'Subir Firma'}
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Formatos: JPG, PNG, GIF. Máximo 5MB. Recomendado: fondo transparente
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' }, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showCTOSignature}
                    onChange={(e) => setFormData({ ...formData, showCTOSignature: e.target.checked })}
                    color="primary"
                  />
                }
                label="Mostrar firma del CTO de Vigilantium en los certificados"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Si se desactiva, los certificados solo mostrarán la firma del firmante de la empresa y del CEO de Vigilantium
              </Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PreviewIcon />}
                onClick={() => setCertificatePreviewOpen(true)}
                sx={{ textTransform: 'none', mb: 1 }}
              >
                Vista preliminar del certificado
              </Button>
              <Typography variant="caption" display="block" color="text.secondary">
                Muestra el diseño con el nombre de empresa, logo y firmas configurados arriba. El nombre del curso y del participante son solo de ejemplo.
              </Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="h6" gutterBottom>
                Dominios Autorizados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Los usuarios solo podrán registrarse con emails de estos dominios
              </Typography>
              <TextField
                fullWidth
                label="Dominios (separados por comas)"
                placeholder="ejemplo: techcorp.com, empresa.com, subdominio.empresa.com"
                value={domainsText}
                onChange={(e) => {
                  setDomainsText(e.target.value);
                }}
                multiline
                rows={3}
                helperText="Ingrese los dominios autorizados separados por comas. Ejemplo: techcorp.com, empresa.com"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCompany ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <CertificateDialog
        open={certificatePreviewOpen}
        onClose={() => setCertificatePreviewOpen(false)}
        dialogTitle="Vista preliminar del certificado"
        studentName="Nombre del participante"
        courseName="Curso de ejemplo — capacitación en ciberseguridad"
        completionDate={new Date().toISOString()}
        position="Completado"
        companyName={formData.name?.trim() || 'Nombre de la empresa'}
        companyLogo={
          logoPreview ||
          (formData.logo ? buildLogoUrl(formData.logo) : undefined) ||
          undefined
        }
        certificateSigner={{
          firstName: formData.certificateSigner.firstName || undefined,
          lastName: formData.certificateSigner.lastName || undefined,
          position: formData.certificateSigner.position || undefined,
          signature:
            signaturePreview ||
            (formData.certificateSigner.signature
              ? buildSignatureUrl(formData.certificateSigner.signature)
              : undefined) ||
            undefined
        }}
        showCTOSignature={formData.showCTOSignature}
        hideShareButtons
        allowViewCertificates
      />

      {/* Dialog para administrar departamentos */}
      <Dialog 
        open={departmentsDialogOpen} 
        onClose={() => {
          setDepartmentsDialogOpen(false);
          setSelectedCompanyForManagement(null);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AccountTreeIcon />
            <Typography variant="h6">
              Administrar Departamentos - {selectedCompanyForManagement?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DepartmentsManagementDialogContent 
            companyId={selectedCompanyForManagement?._id || ''}
            onClose={() => {
              setDepartmentsDialogOpen(false);
              setSelectedCompanyForManagement(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog para administrar sedes */}
      <Dialog 
        open={officesDialogOpen} 
        onClose={() => {
          setOfficesDialogOpen(false);
          setSelectedCompanyForManagement(null);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <LocationIcon />
            <Typography variant="h6">
              Administrar Sedes - {selectedCompanyForManagement?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <OfficesManagementDialogContent 
            companyId={selectedCompanyForManagement?._id || ''}
            onClose={() => {
              setOfficesDialogOpen(false);
              setSelectedCompanyForManagement(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Componente para administrar departamentos dentro del diálogo
const DepartmentsManagementDialogContent: React.FC<{ companyId: string; onClose: () => void }> = ({ companyId, onClose }) => {
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    manager: '',
    maxEmployees: '',
    allowSelfRegistration: true
  });
  const [formLoading, setFormLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadDepartments();
    loadEmployees();
  }, [companyId]);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await api.getDepartments(companyId);
      setDepartments(response);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error cargando departamentos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await api.getCompanyEmployees({ companyId });
      setEmployees(response.employees || []);
    } catch (err: any) {
      console.error('Error cargando empleados:', err);
    }
  };

  const handleOpenFormDialog = (department?: any) => {
    if (department) {
      setEditingDepartment(department);
      setFormData({
        name: department.name,
        description: department.description || '',
        manager: department.manager?._id || '',
        maxEmployees: department.settings?.maxEmployees?.toString() || '',
        allowSelfRegistration: department.settings?.allowSelfRegistration ?? true
      });
    } else {
      setEditingDepartment(null);
      setFormData({
        name: '',
        description: '',
        manager: '',
        maxEmployees: '',
        allowSelfRegistration: true
      });
    }
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingDepartment(null);
    setFormData({
      name: '',
      description: '',
      manager: '',
      maxEmployees: '',
      allowSelfRegistration: true
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const departmentData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        manager: formData.manager || null,
        settings: {
          maxEmployees: formData.maxEmployees ? parseInt(formData.maxEmployees) : null,
          allowSelfRegistration: formData.allowSelfRegistration
        }
      };

      if (editingDepartment) {
        await api.updateDepartment(editingDepartment._id, departmentData);
        setSnackbar({ open: true, message: 'Departamento actualizado exitosamente', severity: 'success' });
      } else {
        await api.createDepartment(departmentData, companyId);
        setSnackbar({ open: true, message: 'Departamento creado exitosamente', severity: 'success' });
      }

      handleCloseFormDialog();
      loadDepartments();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error guardando departamento', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteDepartment = async (department: any) => {
    if (!window.confirm(`¿Está seguro de que desea eliminar el departamento "${department.name}"?`)) {
      return;
    }

    try {
      await api.deleteDepartment(department._id);
      setSnackbar({ open: true, message: 'Departamento eliminado exitosamente', severity: 'success' });
      loadDepartments();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error eliminando departamento', severity: 'error' });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Total: {departments.length} departamentos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenFormDialog()}
          size="small"
        >
          Nuevo Departamento
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Typography>Cargando departamentos...</Typography>
        </Box>
      ) : departments.length === 0 ? (
        <Box textAlign="center" p={3}>
          <Typography variant="body2" color="text.secondary">
            No hay departamentos registrados
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Empleados</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departments.map((department) => (
                <TableRow key={department._id}>
                  <TableCell>
                    <Typography variant="subtitle2">{department.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {department.description || 'Sin descripción'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={department.isActive ? 'Activo' : 'Inactivo'}
                      color={department.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {department.stats?.totalEmployees || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenFormDialog(department)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteDepartment(department)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog de formulario */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDepartment ? 'Editar Departamento' : 'Nuevo Departamento'}</DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Nombre del departamento"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Gerente del departamento</InputLabel>
                <Select
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  label="Gerente del departamento"
                >
                  <MenuItem value="">Sin gerente asignado</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} ({employee.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Máximo de empleados (opcional)"
                type="number"
                value={formData.maxEmployees}
                onChange={(e) => setFormData({ ...formData, maxEmployees: e.target.value })}
                inputProps={{ min: 1 }}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Permitir auto-registro</InputLabel>
                <Select
                  value={formData.allowSelfRegistration ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, allowSelfRegistration: e.target.value === 'true' })}
                  label="Permitir auto-registro"
                >
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog} disabled={formLoading}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={formLoading}>
              {formLoading ? 'Guardando...' : (editingDepartment ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Componente para administrar sedes dentro del diálogo
const OfficesManagementDialogContent: React.FC<{ companyId: string; onClose: () => void }> = ({ companyId, onClose }) => {
  const [offices, setOffices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    manager: '',
    maxEmployees: '',
    allowSelfRegistration: true
  });
  const [formLoading, setFormLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadOffices();
    loadEmployees();
  }, [companyId]);

  const loadOffices = async () => {
    try {
      setLoading(true);
      const response = await api.getOffices(companyId);
      setOffices(response);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error cargando sedes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await api.getCompanyEmployees({ companyId });
      setEmployees(response.employees || []);
    } catch (err: any) {
      console.error('Error cargando empleados:', err);
    }
  };

  const handleOpenFormDialog = (office?: any) => {
    if (office) {
      setEditingOffice(office);
      setFormData({
        name: office.name,
        description: office.description || '',
        address: office.address || '',
        manager: office.manager?._id || '',
        maxEmployees: office.settings?.maxEmployees?.toString() || '',
        allowSelfRegistration: office.settings?.allowSelfRegistration ?? true
      });
    } else {
      setEditingOffice(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        manager: '',
        maxEmployees: '',
        allowSelfRegistration: true
      });
    }
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingOffice(null);
    setFormData({
      name: '',
      description: '',
      address: '',
      manager: '',
      maxEmployees: '',
      allowSelfRegistration: true
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const officeData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        address: formData.address.trim(),
        manager: formData.manager || null,
        settings: {
          maxEmployees: formData.maxEmployees ? parseInt(formData.maxEmployees) : null,
          allowSelfRegistration: formData.allowSelfRegistration
        }
      };

      if (editingOffice) {
        await api.updateOffice(editingOffice._id, officeData);
        setSnackbar({ open: true, message: 'Sede actualizada exitosamente', severity: 'success' });
      } else {
        await api.createOffice(officeData, companyId);
        setSnackbar({ open: true, message: 'Sede creada exitosamente', severity: 'success' });
      }

      handleCloseFormDialog();
      loadOffices();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error guardando sede', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOffice = async (office: any) => {
    if (!window.confirm(`¿Está seguro de que desea eliminar la sede "${office.name}"?`)) {
      return;
    }

    try {
      await api.deleteOffice(office._id);
      setSnackbar({ open: true, message: 'Sede eliminada exitosamente', severity: 'success' });
      loadOffices();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error eliminando sede', severity: 'error' });
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Total: {offices.length} sedes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenFormDialog()}
          size="small"
        >
          Nueva Sede
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Typography>Cargando sedes...</Typography>
        </Box>
      ) : offices.length === 0 ? (
        <Box textAlign="center" p={3}>
          <Typography variant="body2" color="text.secondary">
            No hay sedes registradas
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Dirección</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Empleados</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offices.map((office) => (
                <TableRow key={office._id}>
                  <TableCell>
                    <Typography variant="subtitle2">{office.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {office.address || 'Sin dirección'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={office.isActive ? 'Activa' : 'Inactiva'}
                      color={office.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {office.stats?.totalEmployees || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenFormDialog(office)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteOffice(office)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog de formulario */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOffice ? 'Editar Sede' : 'Nueva Sede'}</DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Nombre de la sede"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              <TextField
                label="Dirección"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Gerente de la sede</InputLabel>
                <Select
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  label="Gerente de la sede"
                >
                  <MenuItem value="">Sin gerente asignado</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} ({employee.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Máximo de empleados (opcional)"
                type="number"
                value={formData.maxEmployees}
                onChange={(e) => setFormData({ ...formData, maxEmployees: e.target.value })}
                inputProps={{ min: 1 }}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Permitir auto-registro</InputLabel>
                <Select
                  value={formData.allowSelfRegistration ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, allowSelfRegistration: e.target.value === 'true' })}
                  label="Permitir auto-registro"
                >
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog} disabled={formLoading}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={formLoading}>
              {formLoading ? 'Guardando...' : (editingOffice ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminCompanies; 